import React, { forwardRef, memo } from "react";
import { Typography } from "./Typography";
import { Input } from "./input";
import { Label } from "./label";
import type { FieldError, UseFormRegisterReturn } from "react-hook-form";
import { cn } from "../lib/utils";

type InputFormFieldProps = {
  label?: string;
  required?: boolean;
  error?: string | FieldError;
  registration?: UseFormRegisterReturn;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
} & React.InputHTMLAttributes<HTMLInputElement>;

const InputFormField = memo(
  forwardRef<HTMLInputElement, InputFormFieldProps>(
    (
      {
        label,
        required,
        error,
        registration,
        leftIcon,
        rightIcon,
        onRightIconClick,
        ...props
      },
      ref,
    ) => {
      const errorMessage = typeof error === "string" ? error : error?.message;
      return (
        <div className="flex flex-col gap-1 w-full">
          {/* Label */}
          {label && (
            <Label className="text-sm font-medium text-brand-black-2">
              {label}{" "}
              {required && (
                <span className="text-brand-error-red text-xl align-middle">
                  *
                </span>
              )}
            </Label>
          )}

          {/* Input Container */}
          <div
            className={cn(
              "flex items-center rounded-lg py-1.5 h-9 border relative",
              error ? "border-brand-error-red" : "border-brand-gray-light",
            )}
          >
            {leftIcon && (
              <span className="mr-2 text-brand-black-2 absolute left-2">
                {leftIcon}
              </span>
            )}

            <Input
              ref={ref}
              className="w-full bg-transparent border-none shadow-none outline-none focus-visible:ring-0 px-3 py-0"
              {...registration}
              {...props}
            />

            {rightIcon && (
              <span
                onClick={onRightIconClick}
                className="cursor-pointer text-brand-black-2 absolute right-2"
              >
                {rightIcon}
              </span>
            )}
          </div>

          {/* Error */}
          {error && (
            <Typography className="text-xs text-destructive">
              {errorMessage}
            </Typography>
          )}
        </div>
      );
    },
  ),
);

InputFormField.displayName = "InputFormField";

export default InputFormField;
